import React, { useEffect, useRef, useState, type ElementRef } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { ClipboardCheck, Check, Pencil } from 'lucide-react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { usePanelStack } from '../layout/PanelStack';
import { useChecklistStore } from '../../store/checklistStore';
import type { InsuranceChecklist } from '../../types';

export interface SelectedChecklistItem {
  id: string;
  label?: string;
}

interface Props {
  structureId: string | undefined;
  value: SelectedChecklistItem | null;
  onChange: (item: SelectedChecklistItem | null) => void;
}

/**
 * "Link to checklist item" row + drill-in. Resolves the linked structure's
 * checklist (claim-assigned or stakeholder default) and lets the poster tag
 * the observation with the step it documents. Renders nothing when there's
 * no structure or no checklist for it.
 *
 * The picker is a PanelStack push (panel-system.md Phase 3) — this section
 * must therefore only ever render inside PostComposer's PanelStack.
 */
export function ChecklistItemPickerSection({ structureId, value, onChange }: Props) {
  const { colors } = useTheme();
  const { push, pop } = usePanelStack();
  const rowRef = useRef<ElementRef<typeof TouchableOpacity>>(null);
  const getChecklistForStructure = useChecklistStore((s) => s.getChecklistForStructure);
  const [checklist, setChecklist] = useState<InsuranceChecklist | null>(null);

  useEffect(() => {
    let stale = false;
    setChecklist(null);
    if (!structureId) return;
    getChecklistForStructure(structureId)
      .then((c) => {
        if (!stale) setChecklist(c);
      })
      .catch(() => {
        // No checklist — the row just doesn't render.
      });
    return () => {
      stale = true;
    };
  }, [structureId, getChecklistForStructure]);

  // Posts arriving via ?checklistItemId= carry an id but no label; fill it in
  // once the checklist loads so the row (and the stored post) show the step
  // name rather than a raw id.
  useEffect(() => {
    if (!checklist || !value || value.label) return;
    const item = checklist.items.find((i) => i.id === value.id);
    if (item) onChange({ id: item.id, label: item.label });
  }, [checklist, value, onChange]);

  if (!structureId || !checklist || checklist.items.length === 0) return null;

  const openPicker = () => {
    push({
      key: 'checklist-item',
      title: 'Which checklist step does this document?',
      triggerRef: rowRef,
      render: () => (
        <ChecklistItemPickerBody
          checklist={checklist}
          selectedId={value?.id ?? null}
          onSelect={(item) => {
            onChange(item);
            pop();
          }}
        />
      ),
    });
  };

  return (
    <>
      <TouchableOpacity
        ref={rowRef}
        onPress={openPicker}
        activeOpacity={0.7}
        style={[styles.row, { borderBottomColor: colors.border }]}>
        <ClipboardCheck size={16} color={colors.text} />
        <Text style={[styles.rowText, { color: colors.textSecondary }]} numberOfLines={1}>
          {value?.label || value?.id || 'Link to checklist item (optional)'}
        </Text>
        <Pencil size={14} color={colors.textTertiary} />
      </TouchableOpacity>
      <Text style={[styles.hint, { color: colors.textSecondary }]}>
        {value
          ? `Checklist item for ${checklist.insurerName}`
          : 'Show which preparation step this photo documents'}
      </Text>
    </>
  );
}

interface BodyProps {
  checklist: InsuranceChecklist;
  selectedId: string | null;
  onSelect: (item: SelectedChecklistItem | null) => void;
}

function ChecklistItemPickerBody({ checklist, selectedId, onSelect }: BodyProps) {
  const { colors } = useTheme();

  return (
    <ScrollView testID="checklist-item-picker-sheet" contentContainerStyle={styles.sheetContent}>
      <Text style={[styles.sheetBlurb, { color: colors.textSecondary }]}>
        {checklist.insurerName} checklist
      </Text>

      {/* Hairline rows (mockup .rowlink); the chosen step is ink, not a
          tinted box. */}
      <View style={[styles.items, { borderTopColor: colors.border }]}>
        {checklist.items.map((item) => {
          const isSelected = item.id === selectedId;
          return (
            <TouchableOpacity
              key={item.id}
              onPress={() => onSelect({ id: item.id, label: item.label })}
              accessibilityRole="radio"
              // RN-web drops accessibilityState; aria-checked is the prop
              // that reaches the DOM.
              aria-checked={isSelected}
              accessibilityLabel={item.label}
              style={[styles.itemRow, { borderBottomColor: colors.border }]}>
              <View style={styles.itemText}>
                <Text
                  style={[
                    styles.itemLabel,
                    isSelected && styles.itemLabelSelected,
                    { color: colors.text },
                  ]}>
                  {item.label}
                </Text>
                {item.description ? (
                  <Text
                    style={[styles.itemDescription, { color: colors.textSecondary }]}
                    numberOfLines={2}>
                    {item.description}
                  </Text>
                ) : null}
              </View>
              {isSelected && <Check size={16} color={colors.text} />}
            </TouchableOpacity>
          );
        })}
      </View>

      {selectedId && (
        <TouchableOpacity
          onPress={() => onSelect(null)}
          accessibilityRole="button"
          accessibilityLabel="Remove link"
          style={styles.clearRow}>
          <Text style={[styles.clearText, { color: colors.danger }]}>Remove link</Text>
        </TouchableOpacity>
      )}
      {/* Scroll sentinel per sheet conventions. */}
      <View testID="checklist-item-picker-sheet-end" style={styles.scrollSentinel} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  // Hairline row, not a tinted box (mockup .rowlink) — matches the sheet's
  // own rows and the post-detail rowlinks.
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: SPACING.smd,
    gap: SPACING.sm,
    borderBottomWidth: 1,
  },
  rowText: {
    ...TYPOGRAPHY.bodySmall,
    flex: 1,
  },
  hint: {
    ...TYPOGRAPHY.bodySmall,
    marginTop: SPACING.xs,
    marginBottom: SPACING.md,
  },
  sheetContent: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  sheetBlurb: {
    ...TYPOGRAPHY.bodySmall,
    marginBottom: SPACING.md,
  },
  items: {
    borderTopWidth: 1,
  },
  itemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.smd,
    borderBottomWidth: 1,
  },
  itemText: {
    flex: 1,
    gap: 2,
  },
  itemLabel: {
    ...TYPOGRAPHY.label,
  },
  itemLabelSelected: {
    fontFamily: TYPOGRAPHY.h4.fontFamily, // semibold when chosen
  },
  itemDescription: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 16,
  },
  clearRow: {
    alignItems: 'center',
    paddingVertical: SPACING.smd,
  },
  clearText: {
    ...TYPOGRAPHY.label,
  },
  scrollSentinel: {
    height: 1,
  },
});
